/**
 * Get the correct API base URL based on environment
 * Ensures consistency across the application and prevents localhost hardcoding
 *
 * Priority:
 * 1. VITE_API_URL env var (for custom backend in dev)
 * 2. '/api' in development (uses Vite proxy)
 * 3. window.location.origin + '/api' in production
 *
 * @returns API base URL without trailing slash
 */
export const getApiBaseUrl = (): string => {
  // Check if VITE_API_URL is explicitly set (for development with custom backend)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  // In development, use relative URL (Vite proxy will handle it)
  if (import.meta.env.DEV) {
    return '/api'
  }

  // In production, use the current origin with /api path
  // This ensures we always use the correct domain (development.xengolf.fi, production, etc.)
  return `${window.location.origin}/api`
}

/**
 * OAuth provider names supported by the backend
 */
type OAuthProvider = 'google' | 'apple'

/**
 * Get OAuth authorization URL for a provider
 * @param provider - OAuth provider name (google, apple)
 * @returns Full OAuth authorization URL
 */
export const getOAuthUrl = (provider: OAuthProvider | string): string => {
  return `${getApiBaseUrl()}/oauth2/authorization/${provider}`
}
